// crewbit-admin/src/components/schedule/ResourceColumn.tsx
//
// Schedule Module Phase C C1.5 — single-column shell for a resource (operator).
// Body parallel to mobile crewbit-mobile/components/schedule/ResourceColumn.tsx
// modulo runtime per feedback_admin_pair_must_match_mobile_body.md.
//
// Spec: docs/references/schedule/SCHEDULE_MODULE_SPEC_v1.md §2.3 + §2.6.

import { Fragment } from 'react';
import { palette, admin } from '../../styles/tokens';
import type { ScheduleEntry } from '../../types/schedule';
import { EntryTile } from './EntryTile';
import { TravelSliver } from './TravelSliver';

export interface ResourceColumnOperator {
  id: string;
  display_name: string;
  avatar_url?: string | null;
}

export interface ResourceColumnProps {
  operator: ResourceColumnOperator;
  entries: ScheduleEntry[];
  travelBufferMinutes: number;
  onEntryTap: (entryId: string) => void;
  onEntryLongPress?: (entryId: string) => void;
  showTravelSlivers?: boolean;
  industryAccent?: string;
  /** Cross-column drop handler — admin-only; called when a tile dragged from another column lands here. */
  onResourceDrop?: (droppedEntryId: string, newResourceId: string) => void;
  draggable?: boolean;
}

export function ResourceColumn({
  operator,
  entries,
  travelBufferMinutes,
  onEntryTap,
  onEntryLongPress,
  showTravelSlivers,
  industryAccent,
  onResourceDrop,
  draggable,
}: ResourceColumnProps) {
  const sorted = [...entries].sort((a, b) => a.start_at.localeCompare(b.start_at));

  return (
    <div
      style={{
        flex: 1, minWidth: 220,
        backgroundColor: admin.surface,
        borderRadius: 12, overflow: 'hidden',
        border: `1px solid ${admin.border}`,
      }}
      onDragOver={onResourceDrop ? (e) => { e.preventDefault(); } : undefined}
      onDrop={onResourceDrop ? (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/entry-id');
        if (id) onResourceDrop(id, operator.id);
      } : undefined}
      data-resource-id={operator.id}
    >
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '8px 12px',
        borderBottom: `1px solid ${admin.border}`,
      }}>
        {operator.avatar_url ? (
          <img src={operator.avatar_url} alt="" style={{ width: 28, height: 28, borderRadius: 14 }} />
        ) : (
          <div style={{
            width: 28, height: 28, borderRadius: 14, backgroundColor: palette.g200,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700, color: palette.g700,
          }}>
            {operator.display_name.slice(0, 1).toUpperCase()}
          </div>
        )}
        <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {operator.display_name}
        </span>
      </div>

      <div style={{ padding: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {sorted.length === 0 ? (
          <div style={{ fontSize: 12, color: palette.g500, textAlign: 'center', padding: '24px 0' }}>
            No entries
          </div>
        ) : (
          sorted.map((entry, idx) => {
            const next = sorted[idx + 1];
            const sliverEligible =
              showTravelSlivers === true &&
              next != null &&
              entry.location_lat != null &&
              entry.location_lng != null &&
              next.location_lat != null &&
              next.location_lng != null;
            return (
              <Fragment key={entry.id}>
                <EntryTile
                  entry={entry}
                  onTap={onEntryTap}
                  onLongPress={onEntryLongPress}
                  density="normal"
                  industryAccent={industryAccent}
                  draggable={draggable}
                  onDragStart={(e) => {
                    e.dataTransfer.setData('text/entry-id', entry.id);
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                />
                {sliverEligible ? (
                  <TravelSliver
                    fromEntry={entry}
                    toEntry={next}
                    travelBufferMinutes={travelBufferMinutes}
                  />
                ) : null}
              </Fragment>
            );
          })
        )}
      </div>
    </div>
  );
}
